import { ChipInput } from "@/components/profile/ChipInput";
import { MultiSelectChips } from "@/components/profile/MultiSelectChips";
import { Textarea } from "@/components/ui/textarea";
import type { GiftPreferencesObject } from "@/lib/teamMemberProfileHints";

export const HOBBY_SUGGESTIONS = [
  "gardening", "cooking", "running", "photography", "reading",
  "gaming", "pottery", "k-drama", "climbing", "cycling",
  "music", "travel", "yoga", "DIY", "puzzles",
];

export const FOOD_SUGGESTIONS = [
  "vanilla", "dark chocolate", "matcha", "pistachio", "sourdough",
  "caramel", "coffee", "cheese", "ramen", "pad thai", "tiramisu",
];

export const VIBE_OPTIONS = [
  { value: "thoughtful", label: "Thoughtful" },
  { value: "calming", label: "Calming" },
  { value: "elegant", label: "Elegant" },
  { value: "fun", label: "Fun" },
  { value: "playful", label: "Playful" },
  { value: "adventurous", label: "Adventurous" },
];

export const AVOID_OPTIONS = [
  { value: "alcohol", label: "Alcohol" },
  { value: "flowers", label: "Flowers" },
  { value: "strong_fragrances", label: "Strong fragrances" },
  { value: "novelty_gifts", label: "Novelty gifts" },
  { value: "spicy_food", label: "Spicy food" },
];

export const CATEGORY_OPTIONS = [
  { value: "cake", label: "Cakes" },
  { value: "florist", label: "Flowers" },
  { value: "hamper", label: "Hampers" },
  { value: "workshop", label: "Workshops" },
  { value: "spa", label: "Spas" },
  { value: "experience", label: "Experiences" },
];

export interface GiftContextFieldValues {
  hobbies: string[];
  favouriteFoods: string[];
  personalNotes: string;
  giftPreferences: GiftPreferencesObject;
}

interface TeamMemberGiftContextFieldsProps {
  values: GiftContextFieldValues;
  onChange: (next: GiftContextFieldValues) => void;
  /** Optional first name for microcopy. */
  firstName?: string;
}

export function TeamMemberGiftContextFields({
  values,
  onChange,
  firstName = "them",
}: TeamMemberGiftContextFieldsProps) {
  const gp = values.giftPreferences;

  const patch = (partial: Partial<GiftContextFieldValues>) =>
    onChange({ ...values, ...partial });

  const patchGiftPrefs = (partial: Partial<GiftPreferencesObject>) =>
    patch({ giftPreferences: { ...gp, ...partial } });

  return (
    <div className="space-y-5">
      <ChipInput
        label="Hobbies"
        values={values.hobbies}
        onChange={(hobbies) => patch({ hobbies })}
        suggestions={HOBBY_SUGGESTIONS}
        placeholder="Add a hobby…"
        hint="Helps Lembra suggest experiences and treats they'll actually enjoy."
      />

      <ChipInput
        label="Favourite foods"
        values={values.favouriteFoods}
        onChange={(favouriteFoods) => patch({ favouriteFoods })}
        suggestions={FOOD_SUGGESTIONS}
        placeholder="Add a favourite food…"
      />

      <div className="space-y-4 rounded-xl bg-butter/25 p-4">
        <p
          className="text-sm font-semibold text-navy"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Gift preferences
        </p>

        <MultiSelectChips
          label="Vibe"
          options={VIBE_OPTIONS}
          selected={gp.vibes ?? []}
          onChange={(vibes) => patchGiftPrefs({ vibes })}
        />

        <MultiSelectChips
          label="Avoid"
          options={AVOID_OPTIONS}
          selected={gp.avoid ?? []}
          onChange={(avoid) => patchGiftPrefs({ avoid })}
          treatment="avoid"
        />

        <MultiSelectChips
          label="Preferred categories"
          options={CATEGORY_OPTIONS}
          selected={gp.desired_categories ?? []}
          onChange={(desired_categories) => patchGiftPrefs({ desired_categories })}
        />

        <div className="space-y-2">
          <label
            className="block text-sm font-semibold text-navy"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            Notes
          </label>
          <Textarea
            value={gp.notes ?? ""}
            onChange={(e) => patchGiftPrefs({ notes: e.target.value })}
            placeholder={`Anything specific about how ${firstName} likes to receive gifts…`}
            className="min-h-[60px]"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label
          className="block text-sm font-semibold text-navy"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Personal notes
        </label>
        <Textarea
          value={values.personalNotes}
          onChange={(e) => patch({ personalNotes: e.target.value })}
          placeholder={`Anything else that would help pick the right gift for ${firstName}…`}
          className="min-h-[60px]"
        />
      </div>
    </div>
  );
}

export function emptyGiftContextValues(): GiftContextFieldValues {
  return {
    hobbies: [],
    favouriteFoods: [],
    personalNotes: "",
    giftPreferences: {},
  };
}

export function giftContextFromMember(member: {
  hobbies?: string[] | null;
  favourite_foods?: string[] | null;
  personal_notes?: string | null;
  gift_preferences?: string[] | GiftPreferencesObject | null;
}): GiftContextFieldValues {
  const raw = member.gift_preferences;
  const gp: GiftPreferencesObject = Array.isArray(raw)
    ? { vibes: raw }
    : (raw ?? {});
  return {
    hobbies: member.hobbies ?? [],
    favouriteFoods: member.favourite_foods ?? [],
    personalNotes: member.personal_notes ?? "",
    giftPreferences: {
      vibes: gp.vibes ?? [],
      avoid: gp.avoid ?? [],
      desired_categories: gp.desired_categories ?? [],
      notes: gp.notes ?? "",
    },
  };
}

export function giftContextToPayload(values: GiftContextFieldValues): {
  hobbies: string[];
  favourite_foods: string[];
  personal_notes: string | null;
  gift_preferences: GiftPreferencesObject | null;
} {
  const gp = values.giftPreferences;
  const hasGiftPrefs =
    (gp.vibes ?? []).length > 0 ||
    (gp.avoid ?? []).length > 0 ||
    (gp.desired_categories ?? []).length > 0 ||
    (gp.notes?.trim().length ?? 0) > 0;

  return {
    hobbies: values.hobbies,
    favourite_foods: values.favouriteFoods,
    personal_notes: values.personalNotes.trim() || null,
    gift_preferences: hasGiftPrefs
      ? {
          vibes: gp.vibes ?? [],
          avoid: gp.avoid ?? [],
          desired_categories: gp.desired_categories ?? [],
          notes: gp.notes?.trim() || undefined,
        }
      : null,
  };
}
